import { RootStore, Store } from '@sama/app-state';
import { matches } from '@sama/micro-dash';
import { mixInInjectableSuperclass } from '@sama/ng-core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ShapeState } from '../annotation/shared/state/shape-state';
import { getInputUrls } from '../misc-utils';
import { logStateToDevtools } from '../test-helpers/state-logging';
import { AbstractLayerSettingsState } from '../toolbars/layer-settings/abstract-layer-settings-state';
import { AbstractSensorContentState } from './abstract-sensor-content-state';

export abstract class AbstractSensorContentStore<
  T extends AbstractSensorContentState = AbstractSensorContentState,
  Shape extends ShapeState = ShapeState,
  FrameType = unknown,
> extends mixInInjectableSuperclass(RootStore)<T> {
  focusedShapeStore$ = this.buildFocusedShapeStore$();

  focusedShape$ = this.focusedShapeStore$.pipe(
    switchMap((focusedShapeStore) => focusedShapeStore?.$ ?? of(undefined)),
  );
  currentSensorFrame$ = this.buildCurrentSensorFrame$();

  constructor(initialState: T, storeName: string) {
    super(initialState);
    this.manage(logStateToDevtools(this, storeName));
  }

  getInputUrl(): string | undefined {
    const { project, output, task, taskExtras } = this.state();
    const input = project.inputs.find(matches({ id: output.meta.input_id }))!;
    return getInputUrls(task, input.name, taskExtras?.signed_urls)[0];
  }

  abstract getLayerSettingsStore(): Store<AbstractLayerSettingsState>;

  abstract getFocusedShapeStore(): Store<Shape> | undefined;

  abstract getFocusedShapeIndex(): number | undefined;

  protected abstract buildCurrentSensorFrame$(): Observable<
    FrameType | undefined
  >;

  protected abstract buildFocusedShapeStore$(): Observable<
    Store<Shape> | undefined
  >;
}
